// src/components/landing/ContactSection.tsx
import React, { useState } from "react";
import { Mail, Phone, MapPin, Instagram, Twitter, Linkedin, Facebook, Send, Loader2, CheckCircle2 } from "lucide-react";
import { submitUserQuery } from "../../services/UserQuery.api";

const CONTACT_INFO = {
  email: "support@adroj.com",
  phone: "+91 98765 43210",
  address: "Agra, Uttar Pradesh, India",
};

const SOCIAL_LINKS = [
  { icon: Instagram, href: "#", label: "Instagram" },
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Linkedin, href: "#", label: "LinkedIn" },
  { icon: Facebook, href: "#", label: "Facebook" },
];

type Status = "idle" | "submitting" | "success" | "error";

export function ContactSection() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim() || !message.trim()) return;

    setStatus("submitting");
    setError(null);
    try {
      await submitUserQuery({ fullName: fullName.trim(), email: email.trim(), message: message.trim() });
      setStatus("success");
      setFullName("");
      setEmail("");
      setMessage("");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  };

  const inputClass =
    "w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-[#0F172A] placeholder:text-slate-400 focus:border-[#EB4A2D] focus:outline-none focus:ring-2 focus:ring-[#EB4A2D]/15";
  const labelClass = "mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500";

  return (
    <section id="discuss-campaign" className="bg-[#ced8eb] py-20 my-4 " >
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:items-center">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#EB4A2D]">Get in touch</span>
          <h2 className="font-display mt-3 text-3xl text-[#0F172A] sm:text-4xl">Let&apos;s Discuss Your Campaign</h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-500">
            Have questions about our platform? Our team is here to help you succeed.
          </p>

          <div className="mt-8 space-y-4">
            <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-3 text-sm text-slate-600 hover:text-[#0F172A]">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#EB4A2D]/10 text-[#EB4A2D]">
                <Mail className="h-4 w-4" />
              </span>
              {CONTACT_INFO.email}
            </a>
            <a href={`tel:${CONTACT_INFO.phone.replace(/\s+/g, "")}`} className="flex items-center gap-3 text-sm text-slate-600 hover:text-[#0F172A]">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#EB4A2D]/10 text-[#EB4A2D]">
                <Phone className="h-4 w-4" />
              </span>
              {CONTACT_INFO.phone}
            </a>
            <div className="flex items-center gap-3 text-sm text-slate-600">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#EB4A2D]/10 text-[#EB4A2D]">
                <MapPin className="h-4 w-4" />
              </span>
              {CONTACT_INFO.address}
            </div>
          </div>

          <div className="mt-10">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Follow us</span>
            <div className="mt-3 flex items-center gap-2.5">
              {SOCIAL_LINKS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-slate-300 hover:text-[#0F172A]"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_24px_50px_-24px_rgba(15,23,42,0.15)] sm:p-8">
          <h3 className="text-base font-semibold text-[#0F172A]">Send a Message</h3>

          {status === "success" ? (
            <div className="mt-6 flex flex-col items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-10 text-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              <p className="text-sm text-slate-600">
                Thanks — your message has been sent. Our team will get back to you shortly.
              </p>
              <button
                onClick={() => setStatus("idle")}
                className="mt-1 text-xs font-semibold text-[#EB4A2D] hover:underline"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              <div>
                <label className={labelClass}>Full name *</label>
                <input
                  className={inputClass}
                  placeholder="Enter your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Email address *</label>
                <input
                  type="email"
                  className={inputClass}
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Message *</label>
                <textarea
                  className={`${inputClass} min-h-[110px] resize-none`}
                  placeholder="Tell us about your advertising needs..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
              </div>

              {status === "error" && error && (
                <p className="text-xs text-red-500">{error}</p>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#EB4A2D] px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition-transform hover:scale-[1.01] disabled:opacity-60"
              >
                {status === "submitting" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send Message <Send className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}